/**
 * RescheduleBookingButton — admin-direct reschedule action (Prompt 11E.6).
 *
 * Lives in Bookings (not Attendance — the ESLint feature-boundary rule
 * forbids `attendance` importing from `bookings`, see
 * docs/architecture/code-boundaries.md) and is injected into
 * StudentAttendanceTab's per-row action via render-prop at the route
 * composition layer (src/routes/index.tsx), same pattern as
 * StudentBookingsTab/StudentEntitlementsTab.
 *
 * Resolves the booking linked to (session_id, student_id) via the existing
 * `GET /v1/bookings?session_id=&student_id=` filter, then reschedules it
 * with the existing `PATCH /v1/bookings/{id}` (useUpdateBooking) — no new
 * backend endpoint. This IS "admin: direct" per the 11E.6 spec, distinct
 * from the 11E.5 viewer path (which only ever creates a pending
 * reschedule *request* for an admin to action separately).
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Input, Modal, ModalFooter } from '@/components/common';
import { useBookings, useUpdateBooking } from '@features/bookings/api/bookings';
import type { ApiError } from '@core/api/client';

interface Props {
  sessionId: string;
  studentId: string;
}

export const RescheduleBookingButton: React.FC<Props> = ({ sessionId, studentId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newStart, setNewStart] = useState('');
  const { data } = useBookings({ sessionId, studentId, limit: 1 });
  const booking = data?.items?.[0];
  const updateBooking = useUpdateBooking();

  const handleSubmit = async () => {
    if (!booking || !newStart) return;
    const start = new Date(newStart);
    if (Number.isNaN(start.getTime())) {
      toast.error('Pick a valid date & time.');
      return;
    }
    const durationMs =
      new Date(booking.ends_at).getTime() - new Date(booking.starts_at).getTime();
    const end = new Date(start.getTime() + (durationMs > 0 ? durationMs : 45 * 60 * 1000));

    try {
      await updateBooking.mutateAsync({
        id: booking.id,
        body: { starts_at: start.toISOString(), ends_at: end.toISOString() },
      });
      toast.success('Session rescheduled.');
      setIsOpen(false);
      setNewStart('');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not reschedule this session.');
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={!booking}
        onClick={() => setIsOpen(true)}
        data-testid={`candeza-reschedule-${sessionId}-${studentId}`}
        title={booking ? undefined : 'No booking found for this session'}
      >
        Reschedule
      </Button>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Reschedule session"
        size="sm"
        data-testid={`candeza-reschedule-modal-${sessionId}-${studentId}`}
      >
        <Input
          label="New date & time"
          type="datetime-local"
          value={newStart}
          onChange={(e) => setNewStart(e.target.value)}
          data-testid="candeza-reschedule-datetime"
        />
        <ModalFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateBooking.isPending || !newStart}
            data-testid="candeza-reschedule-confirm"
          >
            {updateBooking.isPending ? 'Rescheduling…' : 'Confirm'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default RescheduleBookingButton;
